import {Card} from 'platform/components';
import {Box} from 'platform/foundation';
import {match} from 'ts-pattern';

import {mergeAll} from 'ramda';

import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {
  CommunicationActivityLogDatagrid,
  DmsDataGrid,
  Main,
  openActivityChangeLog,
  PageTab,
} from '@dms/shared';

import {composePath, useRequiredParams} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['commandId'] as const;

export function VehicleActivityLogs() {
  const {id} = useRequiredParams();

  const activityLogQueryModifier = (filter: QueryFilterObject) => mergeAll([filter, {record: id}]);

  const emailActivityLogQueryModifier = (filter: QueryFilterObject) => ({
    ...filter,
    recordId: id,
    customerId: undefined,
  });

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = (data) => {
    const {actionKey, getColumnData} = data;
    const commandId = getColumnData('commandId')?.value;

    match(actionKey).with('detail', () => {
      if (commandId && typeof commandId === 'string') {
        openActivityChangeLog({resourceId: EntityResourceIds.vehicle, commandId, recordId: id});
      } else {
        console.error('no command in row');
      }
    });
  };

  const tabs: PageTab[] = [
    {
      href: composePath(vehiclesRoutes.activityLog, {params: {id}}),
      content: (
        <Card
          title={i18n.t('general.labels.activityLogs')}
          isFullHeight
          key="activity-logs-datagrid"
        >
          <DmsDataGrid
            actionCallback={actionCallback}
            predefinedColumns={PREDEFINED_COLUMNS}
            gridCode="vehicle-audit-log"
            queryModifier={activityLogQueryModifier}
            data-testid={testIds.vehicles.activityLog('activity-logs-datagrid')}
          />
        </Card>
      ),
      id: 'vehicle-audit-log',
      title: i18n.t('general.labels.activityLogs'),
      'data-testid': testIds.vehicles.activityLog('navigation-vehicle-audit-log'),
    },
    {
      href: composePath(vehiclesRoutes.emailActivityLogs, {
        params: {id},
      }),
      content: (
        <Card
          title={i18n.t('general.labels.emailActivityLogs')}
          isFullHeight
          key="email-activity-logs-datagrid"
        >
          <CommunicationActivityLogDatagrid
            queryModifier={emailActivityLogQueryModifier}
            presetInstanceId="vehicle-email-activity-logs"
            data-testid={testIds.vehicles.emailActivityLogs('activity-logs-datagrid')}
          />
        </Card>
      ),
      id: 'vehicle-email-activity-logs',
      title: i18n.t('general.labels.emailActivityLogs'),
      'data-testid': testIds.vehicles.emailActivityLogs('navigation-vehicle-email-activity-logs'),
    },
  ];

  return (
    <Box flex={1} minWidth={0}>
      <Main tabs={tabs} isFullHeight />
    </Box>
  );
}
